import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as S from '../../pages/FakturowniaInvoices/FakturowniaInvoices.styled';

/**
 * Re-usable date-range preset bar shared by Finance Dashboard, Invoices and
 * Expenses pages. Owns no state — value flows in from the page so the same
 * presets can drive multiple queries. Adding a new preset is one entry in
 * `PRESET_RANGES` plus an i18n key.
 */
export type DatePresetKey =
  | 'thisMonth'
  | 'prevMonth'
  | 'last3Months'
  | 'thisYear'
  | 'custom';

export interface IDateRangeValue {
  from: string;
  to: string;
  preset: DatePresetKey;
}

interface IDateRangePresetsProps {
  value: IDateRangeValue;
  onChange: (next: IDateRangeValue) => void;
  /** Which preset buttons to show. Default covers the financier's daily needs. */
  presets?: Exclude<DatePresetKey, 'custom'>[];
}

const PRESET_RANGES: Record<
  Exclude<DatePresetKey, 'custom'>,
  () => { from: string; to: string }
> = {
  thisMonth: () => ({
    from: moment().startOf('month').format('YYYY-MM-DD'),
    to: moment().endOf('month').format('YYYY-MM-DD'),
  }),
  prevMonth: () => {
    const m = moment().subtract(1, 'month');
    return {
      from: m.clone().startOf('month').format('YYYY-MM-DD'),
      to: m.clone().endOf('month').format('YYYY-MM-DD'),
    };
  },
  last3Months: () => ({
    from: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
    to: moment().endOf('month').format('YYYY-MM-DD'),
  }),
  thisYear: () => ({
    from: moment().startOf('year').format('YYYY-MM-DD'),
    to: moment().endOf('year').format('YYYY-MM-DD'),
  }),
};

const DEFAULT_PRESETS: Exclude<DatePresetKey, 'custom'>[] = [
  'thisMonth',
  'last3Months',
  'thisYear',
];

export const presetRange = (
  id: Exclude<DatePresetKey, 'custom'>,
): { from: string; to: string } => PRESET_RANGES[id]();

export const DateRangePresets = memo(
  ({ value, onChange, presets = DEFAULT_PRESETS }: IDateRangePresetsProps) => {
    const { t } = useTranslation();

    const onPreset = useCallback(
      (id: Exclude<DatePresetKey, 'custom'>) => {
        const range = PRESET_RANGES[id]();
        onChange({ preset: id, from: range.from, to: range.to });
      },
      [onChange],
    );

    const onCustom = useCallback(
      (patch: { from?: string; to?: string }) => {
        onChange({
          preset: 'custom',
          from: patch.from ?? value.from,
          to: patch.to ?? value.to,
        });
      },
      [onChange, value.from, value.to],
    );

    return (
      <S.FilterBar>
        <S.SegmentTabs>
          {presets.map((id) => (
            <S.SegmentTab
              key={id}
              type="button"
              active={value.preset === id}
              onClick={() => onPreset(id)}
            >
              {t(`dateRangePresets.${id}`)}
            </S.SegmentTab>
          ))}
        </S.SegmentTabs>
        <S.FilterGroup>
          <S.FilterLabel>{t('dateRangePresets.from')}</S.FilterLabel>
          <S.NativeInput
            type="date"
            value={value.from}
            onChange={(e) => onCustom({ from: e.target.value })}
            style={{ minWidth: 'auto' }}
          />
        </S.FilterGroup>
        <S.FilterGroup>
          <S.FilterLabel>{t('dateRangePresets.to')}</S.FilterLabel>
          <S.NativeInput
            type="date"
            value={value.to}
            onChange={(e) => onCustom({ to: e.target.value })}
            style={{ minWidth: 'auto' }}
          />
        </S.FilterGroup>
      </S.FilterBar>
    );
  },
);
